import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";

import type { AdminOrdersPartnerWorkload } from "@/features/marketplace/types/marketplace.types";

interface AdminOrdersPartnerWidgetProps {
  partners: AdminOrdersPartnerWorkload[];
  activeVendorIds: string[];
  onSelect: (vendorId: string) => void;
}

export function AdminOrdersPartnerWidget({
  partners,
  activeVendorIds,
  onSelect,
}: AdminOrdersPartnerWidgetProps) {
  const { t, i18n } = useTranslation();
  const formatter = new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language);
  const maximum = Math.max(...partners.map((partner) => partner.openScripts), 1);

  return (
    <section
      aria-labelledby="admin-orders-partners-title"
      className="min-w-0 p-4"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2
          id="admin-orders-partners-title"
          className="text-xs font-medium text-foreground"
        >
          {t(
            "marketplace.ordersDashboard.overview.partners.title",
            "Partner workload",
          )}
        </h2>
        <span className="text-caption text-muted-foreground">
          {t(
            "marketplace.ordersDashboard.overview.partners.subtitle",
            "Open scripts",
          )}
        </span>
      </div>

      {partners.length === 0 ? (
        <p className="flex min-h-28 items-center justify-center text-xs text-muted-foreground">
          {t(
            "marketplace.ordersDashboard.overview.partners.empty",
            "No open partner work",
          )}
        </p>
      ) : (
        <ol className="mt-2">
          {partners.map((partner, index) => {
            const isActive =
              activeVendorIds.length === 1 &&
              activeVendorIds[0] === partner.vendorId;
            return (
              <li key={partner.vendorId}>
                <button
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => onSelect(partner.vendorId)}
                  className={cn(
                    "relative flex min-h-11 w-full cursor-pointer items-center gap-2 overflow-hidden rounded-sm px-1.5 text-left outline-none transition-colors duration-150",
                    "hover:bg-foreground/5 focus-visible:ring-1 focus-visible:ring-ring motion-reduce:transition-none",
                    isActive &&
                      "bg-primary/5 text-primary dark:bg-primary/15",
                  )}
                >
                  <span
                    aria-hidden
                    className="absolute inset-y-1 left-0 bg-primary/8 dark:bg-primary/15"
                    style={{
                      width: `${(partner.openScripts / maximum) * 100}%`,
                    }}
                  />
                  <span className="relative w-4 shrink-0 text-caption tabular-nums text-muted-foreground">
                    {index + 1}
                  </span>
                  <span className="relative min-w-0 flex-1 truncate text-xs text-foreground">
                    {partner.vendorName}
                  </span>
                  {partner.attention > 0 ? (
                    <span className="relative text-caption tabular-nums text-error">
                      {t(
                        "marketplace.ordersDashboard.overview.partners.attention",
                        {
                          count: partner.attention,
                          defaultValue_one: "{{count}} attention",
                          defaultValue_other: "{{count}} attention",
                        },
                      )}
                    </span>
                  ) : null}
                  <span className="relative w-7 text-right text-xs tabular-nums text-foreground">
                    {formatter.format(partner.openScripts)}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
